import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import {
  interestGroupFormLoader,
  interestGroupListLoader,
  interestGroupViewLoader,
} from "../loaders/interestGroup";
import { InterestGroupBreadcrumb } from "../../breadcrumbs/interestGroup";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";
import EntityCreateLoading from "../../pages/entities/loading/EntityCreateLoading";

const { INTERESTGROUP } = ENTITY;

export const interestGroupRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const interestGroupRoute = createRoute({
    getParentRoute: () => parent,
    path: INTERESTGROUP.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: INTERESTGROUP.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: INTERESTGROUP.label.upFirstPlural,
    },
  });

  // List interestGroups

  const interestGroupListRoute = createRoute({
    getParentRoute: () => interestGroupRoute,
    path: "/",
    loader: interestGroupListLoader,
    pendingComponent: () => <EntityListLoading entity={INTERESTGROUP} />,
  }).lazy(() => import("./interestGroup.lazy").then((d) => d.InterestGroupListRoute));

  // Create interestGroup

  const interestGroupCreateRoute = createRoute({
    getParentRoute: () => interestGroupRoute,
    path: "create",
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: INTERESTGROUP.type.singular, action: "CREATE" },
      ]),
    staticData: {
      breadcrumb: "Create new",
    },
    loader: interestGroupFormLoader,
    pendingComponent: () => <EntityCreateLoading entity={INTERESTGROUP} />,
  }).lazy(() => import("./interestGroup.lazy").then((d) => d.InterestGroupCreateRoute));

  // View interestGroup

  const interestGroupViewRootRoute = createRoute({
    getParentRoute: () => interestGroupRoute,
    path: "$interestGroupId",
    staticData: {
      breadcrumb: InterestGroupBreadcrumb,
    },
    loader: interestGroupViewLoader,
  });

  // Edit interestGroup

  const interestGroupEditRoute = createRoute({
    getParentRoute: () => interestGroupViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: interestGroupFormLoader,
    pendingComponent: () => <EntityEditLoading entity={INTERESTGROUP} />,
  }).lazy(() => import("./interestGroup.lazy").then((d) => d.InterestGroupEditRoute));


  // View interestGroup Layout

  const interestGroupViewLayoutRoute = createRoute({
    getParentRoute: () => interestGroupViewRootRoute,
    id: "interestGroup-view-layout",
    pendingComponent: () => <EntityViewLoading entity={INTERESTGROUP} />,
  }).lazy(() =>
    import("./interestGroup.lazy").then((d) => d.InterestGroupViewLayoutRoute)
  );

  // View interestGroup - General Tab

  const interestGroupViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => interestGroupViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./interestGroup.lazy").then((d) => d.InterestGroupViewLayoutGeneralTabRoute)
  );

  // View interestGroup - About Tab

  const interestGroupViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => interestGroupViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./interestGroup.lazy").then((d) => d.InterestGroupViewLayoutAboutTabRoute)
  );


  // Route tree
  interestGroupRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [interestGroupListRoute]),
    interestGroupCreateRoute,
    interestGroupViewRootRoute.addChildren([
      interestGroupEditRoute,
      interestGroupViewLayoutRoute.addChildren([
        interestGroupViewLayoutGeneralTabRoute,
        interestGroupViewLayoutAboutTabRoute,
      ]),
    ]),
  ]);

  return interestGroupRoute;
};

const interestGroupRoute = interestGroupRouteFactory();

export default interestGroupRoute;
